import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  type Availability,
  type Group,
  type PolicyGroup,
  type Session,
} from '~entities/classes/modal/class';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';
import { type Days } from '~shared/constants/days';

export interface GetAllClassesResponse {
  // Basic Information
  _id: string;
  title: string;
  studio: string;
  description: string;
  tags: Array<Group>;
  color: string;
  hide: boolean;

  // Schedule and Timing
  startTime: string;
  endTime: string;
  duration: number;
  startDate: string;
  endDate: string;
  startYear: number;
  endYear: number;
  startMonth: number;
  endMonth: number;
  days: Array<Days>;
  timeConfig: string;
  availability: Array<Availability>;

  // Registration
  registrationStartDate: string;
  registrationEndDate: string | null;
  allowOnlineRegistration: boolean;
  allowPortalEnrollment: boolean;
  allowTrialEnrollment: boolean;
  displayOnWebsite: boolean;

  // Tuition and Fees
  tuitionFee: number;
  costumeFee: number;
  tuitionBillingMethod: string;
  tuitionBillingCycle: string;
  tuitionDiscountRule: string;
  prorateTuition: boolean;
  registrationFee: boolean;
  registrationFeeAmount: number;

  // Capacity
  maxSize: number;
  maxWait: number;
  remainingSeats: number;

  // Associations
  productId_stripe: string;
  productId_ghl: string;
  calendarId_ghl: string;
  session: Session;
  policyGroup: Array<PolicyGroup>;
  location: Group;

  // Personnel
  mainTeacher: string;
  subTeacher: string;
  instructor: Array<Group>;

  // Grouping
  group: Array<Group>;
  room: Group | null;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export const getAllClassesAPI = async () => {
  const response =
    await baseAPI.get<Array<GetAllClassesResponse>>(`/enrollments/get/all`);
  return response.data;
};

export const getAllClassesQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.CLASSES(),
    queryFn: () => getAllClassesAPI(),
    staleTime: 1000 * 60 * 5,
  });

export const useGetAllClasses = () => {
  return useQuery(getAllClassesQueryOptions());
};
